import { auth } from "../config/firebase";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

class ApiClient {
  /**
   * Make public API request (no authentication required)
   */
  async publicRequest(endpoint, options = {}) {
    const headers = {
      "Content-Type": "application/json",
      ...options.headers,
    };

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
      cache: 'no-store',
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.message || "API request failed");
      error.status = response.status;
      throw error;
    }

    return data;
  }

  /**
   * Make authenticated API request
   */
  async request(endpoint, options = {}) {
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    // Force reload user to get fresh token
    await user.reload();
    
    // Get Firebase ID token - force refresh to ensure it's current
    const token = await user.getIdToken(true);
    
    // Decode token to verify UID
    const tokenParts = token.split('.');
    const payload = JSON.parse(atob(tokenParts[1]));
    
    console.log('API Request - User:', user.email, 'UID:', user.uid, 'Endpoint:', endpoint);
    console.log('Token payload UID:', payload.sub);
    console.log('Token payload email:', payload.email);
    
    if (payload.sub !== user.uid) {
      console.error('TOKEN MISMATCH! auth.currentUser.uid:', user.uid, 'but token has UID:', payload.sub);
      throw new Error('Token UID mismatch - please logout and login again');
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options.headers,
    };

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
      cache: 'no-store',
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.message || "API request failed");
      error.status = response.status;
      throw error;
    }

    return data;
  }

  // ============ Profile ============

  async getProfile() {
    return this.request("/profile");
  }

  async updateProfile(profileData) {
    return this.request("/profile", {
      method: "PUT",
      body: JSON.stringify(profileData),
    });
  }

  // ============ Orders (Customer) ============

  async getOrders() {
    return this.request("/orders");
  }

  async getOrder(id) {
    return this.request(`/orders/${id}`);
  }

  async createOrder(orderData) {
    return this.request("/orders", {
      method: "POST",
      body: JSON.stringify(orderData),
    });
  }

  // ============ Membership ============

  async getMembershipPlans() {
    // Public endpoint - no authentication required
    return this.publicRequest("/membership/plans");
  }

  async getMembership() {
    return this.request("/membership");
  }

  async purchaseMembership(planId, paymentMethod = "online") {
    return this.request("/membership/purchase", {
      method: "POST",
      body: JSON.stringify({ plan_id: planId, payment_method: paymentMethod }),
    });
  }

  async getMembershipHistory() {
    return this.request("/membership/history");
  }

  // ============ Items/Pricing Catalog ============

  async getItems() {
    return this.publicRequest("/items");
  }

  async getItemsByCategory(category) {
    return this.publicRequest(`/items?category=${category}`);
  }

  // ============ Admin - Orders ============

  async adminGetOrders() {
    return this.request("/admin/orders");
  }

  async adminGetOrder(id) {
    return this.request(`/admin/orders/${id}`);
  }

  async adminUpdateOrderStatus(id, status) {
    return this.request(`/admin/orders/${id}/status`, {
      method: "PUT",
      body: JSON.stringify({ status }),
    });
  }

  async adminAssignStaff(orderId, staffId) {
    return this.request(`/admin/orders/${orderId}/assign`, {
      method: "PUT",
      body: JSON.stringify({ staff_id: staffId }),
    });
  }

  async adminDeleteOrder(id) {
    return this.request(`/admin/orders/${id}`, {
      method: "DELETE",
    });
  }

  async adminCreateWalkInOrder(orderData) {
    return this.request("/admin/orders/walkin", {
      method: "POST",
      body: JSON.stringify(orderData),
    });
  }

  async adminGetOrderItems(orderId) {
    return this.request(`/admin/orders/${orderId}/items`);
  }

  async getOrderItems(orderId) {
    return this.request(`/orders/${orderId}/items`);
  }

  // ============ Admin - Customers ============

  async adminGetCustomers() {
    return this.request("/admin/customers");
  }

  async adminGetCustomer(id) {
    return this.request(`/admin/customers/${id}`);
  }

  async adminUpdateCustomer(id, customerData) {
    return this.request(`/admin/customers/${id}`, {
      method: "PUT",
      body: JSON.stringify(customerData),
    });
  }

  async adminDeleteCustomer(id) {
    return this.request(`/admin/customers/${id}`, {
      method: "DELETE",
    });
  }

  // ============ Admin - Services ============

  async adminGetServices() {
    return this.request("/admin/services");
  }

  async adminGetService(id) {
    return this.request(`/admin/services/${id}`);
  }

  async adminCreateService(serviceData) {
    return this.request("/admin/services", {
      method: "POST",
      body: JSON.stringify(serviceData),
    });
  }

  async adminUpdateService(id, serviceData) {
    return this.request(`/admin/services/${id}`, {
      method: "PUT",
      body: JSON.stringify(serviceData),
    });
  }

  async adminDeleteService(id) {
    return this.request(`/admin/services/${id}`, {
      method: "DELETE",
    });
  }

  // ============ Admin - Garment Types ============

  async adminGetGarmentTypes() {
    return this.request("/admin/garment-types");
  }

  async adminCreateGarmentType(garmentData) {
    return this.request("/admin/garment-types", {
      method: "POST",
      body: JSON.stringify(garmentData),
    });
  }

  // ============ Admin - Pricing ============

  async adminGetPricing() {
    return this.request("/admin/pricing");
  }

  async adminUpsertPricing(pricingData) {
    return this.request("/admin/pricing", {
      method: "POST",
      body: JSON.stringify(pricingData),
    });
  }

  async adminBulkUpdatePricing(pricingArray) {
    return this.request("/admin/pricing/bulk", {
      method: "POST",
      body: JSON.stringify({ pricing: pricingArray }),
    });
  }

  async adminDeletePricing(id) {
    return this.request(`/admin/pricing/${id}`, {
      method: "DELETE",
    });
  }

  // ============ Admin - Staff ============

  async adminGetStaff() {
    return this.request("/admin/staff");
  }

  async adminGetStaffMember(id) {
    return this.request(`/admin/staff/${id}`);
  }

  async adminCreateStaff(staffData) {
    return this.request("/admin/staff", {
      method: "POST",
      body: JSON.stringify(staffData),
    });
  }

  async adminUpdateStaff(id, staffData) {
    return this.request(`/admin/staff/${id}`, {
      method: "PUT",
      body: JSON.stringify(staffData),
    });
  }

  async adminDeleteStaff(id) {
    return this.request(`/admin/staff/${id}`, {
      method: "DELETE",
    });
  }

  // ============ Admin - Items Catalog ============

  async adminGetItems(filters = {}) {
    const params = new URLSearchParams(filters);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/admin/items${query}`);
  }

  async adminGetItem(id) {
    return this.request(`/admin/items/${id}`);
  }

  async adminCreateItem(itemData) {
    return this.request("/admin/items", {
      method: "POST",
      body: JSON.stringify(itemData),
    });
  }

  async adminUpdateItem(id, itemData) {
    return this.request(`/admin/items/${id}`, {
      method: "PUT",
      body: JSON.stringify(itemData),
    });
  }

  async adminDeleteItem(id) {
    return this.request(`/admin/items/${id}`, {
      method: "DELETE",
    });
  }

  async adminGetCategories() {
    return this.request("/admin/items/categories");
  }

  // ============ Admin - Reports/Analytics ============

  async adminGetReportsSummary(from, to) {
    const params = new URLSearchParams({ from, to });
    return this.request(`/admin/reports/summary?${params.toString()}`);
  }

  async adminGetReportsDaily(from, to) {
    const params = new URLSearchParams({ from, to });
    return this.request(`/admin/reports/daily?${params.toString()}`);
  }

  async adminGetReportsBreakdown(groupBy, from, to) {
    const params = new URLSearchParams({ groupBy, from, to });
    return this.request(`/admin/reports/breakdown?${params.toString()}`);
  }

  async adminGetServiceBreakdown(from, to) {
    const params = new URLSearchParams({ from, to });
    return this.request(`/admin/reports/service-breakdown?${params.toString()}`);
  }

  // ============ Admin - Staff Salary ============

  async adminGetSalaryRecords(filters = {}) {
    const params = new URLSearchParams(filters);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/admin/staff-salary${query}`);
  }

  async adminGetSalaryRecord(id) {
    return this.request(`/admin/staff-salary/${id}`);
  }

  async adminUpsertSalaryRecord(salaryData) {
    return this.request("/admin/staff-salary", {
      method: "POST",
      body: JSON.stringify(salaryData),
    });
  }

  async adminRecordSalaryPayment(salaryId, paymentData) {
    return this.request(`/admin/staff-salary/${salaryId}/payment`, {
      method: "POST",
      body: JSON.stringify(paymentData),
    });
  }

  async adminGetSalaryPayments(salaryId) {
    return this.request(`/admin/staff-salary/${salaryId}/payments`);
  }

  async adminDeleteSalaryRecord(id) {
    return this.request(`/admin/staff-salary/${id}`, {
      method: "DELETE",
    });
  }

  // ============ Admin - Memberships ============

  async adminGetMemberships(status = null) {
    const query = status ? `?status=${status}` : '';
    return this.request(`/admin/memberships${query}`);
  }

  async adminGetMembership(id) {
    return this.request(`/admin/memberships/${id}`);
  }

  async adminActivateMembership(membershipData) {
    return this.request("/admin/memberships", {
      method: "POST",
      body: JSON.stringify(membershipData),
    });
  }

  async adminDeactivateMembership(id) {
    return this.request(`/admin/memberships/${id}`, {
      method: "DELETE",
    });
  }
}

export default new ApiClient();
